import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Avatar, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  InputAdornment, Pagination, Badge,
  Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogActions, DialogContent, DialogTitle
} from '@mui/material';
import {
  History as HistoryIcon,
  ShoppingCart as CartIcon,
  DateRange as DateRangeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types based on API response
interface RequestItem {
  id: string;
  product_id: string;
  name: string;
  quantity: number;
  quantity_approved?: number;
  status: string;
}

interface UserRequest {
  request_id: string;
  requested_by: string;
  approved_by?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  created_at: string;
  items: RequestItem[];
}

interface ApiResponse {
  status: boolean;
  message: string;
  data: UserRequest[];
}

interface FilterOptions {
  startDate: Date | null;
  endDate: Date | null;
  status: 'all' | 'pending' | 'approved' | 'rejected' | 'cancelled';
  search: string;
}

const RequisitionHistoryFb: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  
  // Data states
  const [requests, setRequests] = useState<UserRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<UserRequest[]>([]);
  
  // Filter states
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days
    endDate: new Date(),
    status: 'all',
    search: ''
  });
  
  // UI states
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Helper function to safely parse dates
  const safeParseDate = (dateString: string | undefined): Date => {
    if (!dateString) return new Date();
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString);
      return new Date();
    }
    return date;
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  // Fetch user requests
  const fetchUserRequests = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getMyBarRequestsWithItems_branch`,
        formData,
        config
      );

      console.log("Requests response:", response.data);

      if (response.data.status) {
        setRequests(response.data.data || []);
        applyFilters(response.data.data || []);
      } else {
        showNotification('Failed to fetch requests', 'error');
      }
    } catch (error) {
      console.error("Fetch requests error:", error);
      showNotification('Error fetching requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Delete/cancel request
  const deleteRequest = async (requestId: string) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("request_id", requestId);

      const response = await axios.post(
        `${baseUrl()}BeverageSupervisors/deleteBarRequestOrder`,
        formData,
        config
      );

      if (response.data.status) {
        showNotification('Request cancelled successfully', 'success');
        fetchUserRequests();
      } else {
        showNotification(response.data.message || 'Failed to cancel request', 'error');
      }
    } catch (error: any) {
      console.error("Delete request error:", error);
      showNotification(error.response?.data?.message || 'Error cancelling request', 'error');
    } finally {
      setLoading(false);
      setConfirmOpen(false);
      setSelectedRequestId(null);
    }
  };

  // Apply filters
  const applyFilters = (data: UserRequest[]) => {
    let filtered = [...data];

    // Filter by status
    if (filters.status !== 'all') {
      filtered = filtered.filter(req => req.status === filters.status);
    }

    // Filter by date range
    if (filters.startDate && filters.endDate) {
      const start = filters.startDate.getTime();
      const end = filters.endDate.getTime();
      filtered = filtered.filter(req => {
        const reqDate = safeParseDate(req.created_at).getTime();
        return reqDate >= start && reqDate <= end;
      });
    }

    // Filter by search (request ID)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(req => 
        req.request_id?.toLowerCase().includes(searchLower)
      );
    }

    // Sort by date descending (newest first)
    filtered.sort((a, b) => 
      safeParseDate(b.created_at).getTime() - safeParseDate(a.created_at).getTime()
    );

    setFilteredRequests(filtered);
    setPage(1);
  };

  // Handle filter changes
  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filters when they change
  useEffect(() => {
    applyFilters(requests);
  }, [filters.status, filters.startDate, filters.endDate, filters.search, requests]);

  // Initial load
  useEffect(() => {
    fetchUserRequests();
  }, []);

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchUserRequests().finally(() => setRefreshing(false));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date(),
      status: 'all',
      search: ''
    });
  };

  // Handle cancel click
  const handleCancelClick = (requestId: string) => {
    setSelectedRequestId(requestId);
    setConfirmOpen(true);
  };

  // Handle confirm cancel
  const handleConfirmCancel = () => {
    if (selectedRequestId) {
      deleteRequest(selectedRequestId);
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />;
      case 'rejected':
        return <CancelIcon sx={{ color: '#ef4444', fontSize: 20 }} />;
      case 'pending':
        return <PendingIcon sx={{ color: '#f59e0b', fontSize: 20 }} />;
      case 'cancelled':
        return <WarningIcon sx={{ color: '#6b7280', fontSize: 20 }} />;
      default:
        return <WarningIcon sx={{ color: '#6b7280', fontSize: 20 }} />;
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  // Get status chip
  const getStatusChip = (status: string) => {
    const colors = {
      approved: { bg: '#10b981', color: 'white' },
      rejected: { bg: '#ef4444', color: 'white' },
      pending: { bg: '#f59e0b', color: 'white' },
      cancelled: { bg: '#6b7280', color: 'white' }
    };
    
    const color = colors[status as keyof typeof colors] || colors.cancelled;
    
    return (
      <Chip
        size="small"
        label={status}
        sx={{
          bgcolor: alpha(color.bg, 0.1),
          color: color.bg,
          textTransform: 'capitalize',
          fontWeight: 600
        }}
      />
    );
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all' && v !== '').length;

  // Pagination
  const pageCount = Math.ceil(filteredRequests.length / rowsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getDateRangeLabel = () => {
    if (filters.startDate && filters.endDate) {
      return `${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}`;
    }
    return 'Select date range';
  };

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper'
              }}
            >
              <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                  <IconButton onClick={() => history.goBack()}>
                    <BackIcon />
                  </IconButton>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.main
                    }}
                  >
                    <HistoryIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight={600}>
                      Requisition History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      View and track your stock requests
                    </Typography>
                  </Box>
                  <Chip 
                    label={getDateRangeLabel()}
                    icon={<DateRangeIcon />}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Tooltip title="Refresh">
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                      disabled={refreshing}
                      size="small"
                    >
                      Refresh
                    </Button>
                  </Tooltip>
                </Stack>
              </Container>
            </Paper>

            <Container maxWidth="xl" sx={{ py: 3 }}>
              
              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2 }}>
                    <Typography color="text.secondary" variant="body2">Total Requests</Typography>
                    <Typography variant="h4">{requests.length}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: alpha('#f59e0b', 0.05) }}>
                    <Typography color="text.secondary" variant="body2">Pending</Typography>
                    <Typography variant="h4" color="#f59e0b">
                      {requests.filter(r => r.status === 'pending').length}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: alpha('#10b981', 0.05) }}>
                    <Typography color="text.secondary" variant="body2">Approved</Typography>
                    <Typography variant="h4" color="#10b981">
                      {requests.filter(r => r.status === 'approved').length}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: alpha('#ef4444', 0.05) }}>
                    <Typography color="text.secondary" variant="body2">Rejected</Typography>
                    <Typography variant="h4" color="#ef4444">
                      {requests.filter(r => r.status === 'rejected').length}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Filters */}
              <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Badge badgeContent={activeFilterCount} color="primary">
                        <FilterIcon color="action" />
                      </Badge>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Filters
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Stack>
                </Box>

                {showFilters && (
                  <>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="Start Date"
                            value={filters.startDate}
                            onChange={(date) => handleFilterChange('startDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="End Date"
                            value={filters.endDate}
                            onChange={(date) => handleFilterChange('endDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={filters.status}
                              label="Status"
                              onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                              <MenuItem value="all">All Statuses</MenuItem>
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="approved">Approved</MenuItem>
                              <MenuItem value="rejected">Rejected</MenuItem>
                              <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Search Request ID"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                              endAdornment: filters.search && (
                                <InputAdornment position="end">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleFilterChange('search', '')}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Showing {filteredRequests.length} requests
                        </Typography>
                        <Button
                          size="small"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                        >
                          Clear All
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
              </Paper>

              {/* Requests List */}
              <Paper>
                {loading && paginatedRequests.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : filteredRequests.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Stack spacing={2} alignItems="center">
                      <CartIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                      <Typography color="text.secondary">No requests found</Typography>
                      <Button variant="outlined" size="small" onClick={clearFilters}>
                        Clear Filters
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Stack spacing={0}>
                    {paginatedRequests.map((request) => (
                      <Accordion 
                        key={request.request_id} 
                        sx={{ 
                          '&:before': { display: 'none' }, 
                          boxShadow: 'none',
                          borderBottom: `1px solid ${theme.palette.divider}`
                        }}
                      >
                        <AccordionSummary 
                          expandIcon={<ExpandMoreIcon />} 
                          sx={{ px: 3 }}
                        >
                          <Stack 
                            direction="row" 
                            justifyContent="space-between" 
                            alignItems="center" 
                            sx={{ width: '100%' }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2}>
                              {getStatusIcon(request.status)}
                              <Box>
                                <Typography fontWeight={600} color="text.primary">
                                  Request #{request.request_id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {format(safeParseDate(request.created_at), 'MMM dd, yyyy HH:mm')}
                                </Typography>
                              </Box>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {request.status === 'pending' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelClick(request.request_id);
                                  }}
                                  sx={{ mr: 1 }}
                                >
                                  Cancel
                                </Button>
                              )}
                              {getStatusChip(request.status)}
                            </Stack>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 3, pb: 3 }}>
                          <TableContainer component={Paper} sx={{ borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Requested</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600 }}>Approved</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {request.items.map((item, index) => (
                                  <TableRow key={index} hover>
                                    <TableCell>
                                      <Typography variant="body2">{item.name}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip
                                        label={item.quantity}
                                        size="small"
                                        sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip
                                        label={item.quantity_approved || '-'}
                                        size="small"
                                        sx={{
                                          bgcolor: item.quantity_approved 
                                            ? alpha(theme.palette.success.main, 0.1) 
                                            : alpha(theme.palette.grey[500], 0.1),
                                          color: item.quantity_approved 
                                            ? theme.palette.success.main 
                                            : theme.palette.grey[500]
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      {getStatusChip(item.status)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Stack>
                )}

                {/* Pagination */}
                {filteredRequests.length > 0 && (
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={(e, value) => setPage(value)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </Paper>
            </Container>

            {/* Confirmation Dialog */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon sx={{ color: '#f59e0b' }} />
                <Typography variant="h6">Confirm Cancellation</Typography>
              </DialogTitle>
              <DialogContent>
                <Typography color="text.secondary">
                  Are you sure you want to cancel this request? This action cannot be undone.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button
                  onClick={() => setConfirmOpen(false)}
                  variant="outlined"
                >
                  Keep Request
                </Button>
                <Button
                  onClick={handleConfirmCancel}
                  variant="contained"
                  color="error"
                >
                  Yes, Cancel Request
                </Button>
              </DialogActions>
            </Dialog>

            {/* Refresh Indicator */}
            {refreshing && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 80,
                  right: 24,
                  zIndex: 1100
                }}
              >
                <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="caption">Refreshing...</Typography>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default RequisitionHistoryFb;